import { FC, isValidElement, ReactElement, ReactNode, useState } from "react";
import { Pressable } from "react-native";
import { CheckIcon } from "lucide-react-native";

import { LanguageType } from "@libs/i18n";
import { VnBox } from "@/components/VnBox";
import { VnButton } from "@/components/VnButton/VnButton";
import {
  VnDialog,
  VnDialogContent,
  VnDialogFooter,
  VnDialogHeader,
  VnDialogTitle,
  VnDialogTrigger,
} from "@/components/VnDialog";
import { VnIcon } from "@/components/VnIcon";
import { VnText } from "@/components/VnText/VnText";
import { languageOptions } from "@/domain/languages";
import { useAppLanguage } from "@/hooks/useAppLanguage";
import { useUpdatePreferredLanguage } from "@/hooks/useUpdatePreferredLanguage";
import { i18n } from "@/i18n";
import { cn } from "@/utils/tailwind";

type LanguageSelectorPropsType = {
  children: React.ReactNode;
};

// Components that handle their own touch events and should use asChild
const isTouchableElement = (child: ReactNode): child is ReactElement => {
  if (!isValidElement(child)) return false;
  // VnButton is the main touchable component used as trigger
  return child.type === VnButton;
};

const LanguageSelector: FC<LanguageSelectorPropsType> = ({ children }) => {
  const { language } = useAppLanguage();
  const { updatePreferredLanguage } = useUpdatePreferredLanguage();

  const [selectedLanguage, setSelectedLanguage] =
    useState<LanguageType>(language);

  const handleDone = () => {
    if (selectedLanguage) {
      updatePreferredLanguage(selectedLanguage);
    }
  };

  const isButtonDisabled = selectedLanguage === language || !selectedLanguage;

  // Use asChild when children is a touchable component to avoid nested touchables
  const shouldUseAsChild = isTouchableElement(children);

  return (
    <VnDialog>
      <VnDialogTrigger asChild={shouldUseAsChild}>{children}</VnDialogTrigger>
      <VnDialogContent className="w-screen sm:w-96">
        <VnDialogHeader>
          <VnDialogTitle
            i18nKey="native:languageSelectorModal.title"
            className="flex grow"
          />
        </VnDialogHeader>
        {languageOptions.map(({ language, descriptionI18nKey, locale }) => {
          const isCurrentLanguage = locale === selectedLanguage;
          return (
            <Pressable
              key={locale}
              onPress={() => {
                setSelectedLanguage(locale);
              }}
              className={cn(
                "flex flex-row justify-between rounded p-2.5 pr-3",
                isCurrentLanguage && "bg-secondary",
              )}>
              <VnBox>
                <VnText variant="lg">{`${language} (${i18n.t(descriptionI18nKey)})`}</VnText>
              </VnBox>
              {isCurrentLanguage && <VnIcon as={CheckIcon} size={16} />}
            </Pressable>
          );
        })}
        <VnDialogFooter>
          <VnButton
            className="w-full"
            size="lg"
            i18nKey="common:done"
            onPress={handleDone}
            disabled={isButtonDisabled}
          />
        </VnDialogFooter>
      </VnDialogContent>
    </VnDialog>
  );
};

export { LanguageSelector };
